import DeleteScene from "../../Experience/ObjectManagement/DeleteScene";
import debug from "debug";

const log = debug("app:fn");

/**
 * Class for loading XMD designs and handling related events.
 * @category Components
 * @subcategory XmdManager
 */
class XmdLoader {
  /**
   * Constructs the XmdLoader instance.
   * @param {Object} experience - The experience object containing eventHub and other context.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    // Store bound methods for proper cleanup
    this.boundHandleFileUpload = this.handleFileUpload.bind(this);
    this.setupListeners();
  }

  /**
   * Sets up event listeners for various import and design-related events.
   */
  setupListeners() {
    // New Svelte modal flow - handles file upload from ImportDesignModal
    this.eventHub.on("uploadDesign", this.boundHandleFileUpload);

    this.eventHub.on("importExample:transceiver", () => {
      log("regiestered event transceivier");
      this.showImportDesignConfirmation("importExample:transceiver");
    });

    this.eventHub.on("importExample:layers", () => {
      this.showImportDesignConfirmation("importExample:layers");
    });

    this.eventHub.on("modalImportExample:transceiver", () => {
      this.importExample("modalImportExample:transceiver");
    });

    this.eventHub.on("modalImportExample:layers", () => {
      this.importExample("modalImportExample:layers");
    });

    this.eventHub.on("3d-switch", this.resetLayerSliders.bind(this));
  }

  cleanup() {
    if (!this.eventHub) return; // Prevent cleanup if already disposed

    log("Cleaning up XmdLoader...");

    // Unregister all event listeners
    this.eventHub.off("uploadDesign", this.boundHandleFileUpload);
    this.eventHub.off("importExample:transceiver", this.showImportDesignConfirmation);
    this.eventHub.off("importExample:layers", this.showImportDesignConfirmation);
    this.eventHub.off("modalImportExample:transceiver", this.importExample);
    this.eventHub.off("modalImportExample:layers", this.importExample);

    log("Unregistered all event listeners");

    // Clear references
    this.experience = null;
    this.eventHub = null;

    log("XmdLoader cleanup completed.");
  }

  /**
   * Displays a confirmation modal before importing a design.
   * @param {string} eventName - The name of the event triggering the modal.
   */
  showImportDesignConfirmation(eventName) {
    this.eventHub.emit("modalInfo", {
      title: "Import Design",
      message:
        "If you have any unsaved changes, they will be lost when importing a new design. Do you want to continue?",
      actions: [
        { label: "Cancel", callback: this.cancelCallback.bind(this) },
        {
          label: "Continue",
          callback: () => this.continueImportDesign(eventName),
        },
      ],
      autoClose: false,
    });
  }

  /**
   * Callback executed when the import design operation is canceled.
   */
  cancelCallback() {
    log("Import design operation canceled.");
  }

  /**
   * Continues the import process based on the event name.
   * @param {string} eventName - The event triggering the continuation.
   */
  continueImportDesign(eventName) {
    log(`continueImportDesign triggered by: ${eventName}`);

    if (eventName.startsWith("importDesign")) {
      this.importDesign(eventName);
    } else if (eventName.startsWith("importExample")) {
      this.importExample(eventName);
    } else {
      log(`Unknown event: ${eventName}`);
    }
  }

  /**
   * Handles file upload from the new Svelte ImportDesignModal.
   * This method replicates the exact logic from the original importDesign method.
   * @param {File} file - The file to import.
   */
  handleFileUpload(file) {
    log("handleFileUpload called with file:", file);
    
    if (!file) {
      log("No file provided to handleFileUpload");
      return;
    }

    if (!(file instanceof File)) {
      log("handleFileUpload received non-File object:", typeof file, file);
      // Try to extract file from the object if it was wrapped
      if (file && file.file) {
        file = file.file;
        log("Extracted file from wrapper:", file);
      } else {
        log("Could not extract file from object");
        return;
      }
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xmd") {
      this.eventHub.emit("modalInfo", {
        title: "Information",
        message: "Please choose a .xmd file.",
        actions: [
          {
            label: "OK",
            callback: () => {
              log("OK button clicked");
            },
          },
        ],
        autoClose: false,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = e.target.result;
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      log("File read successfully, name:", nameWithoutExtension, "content length:", fileContent.length);
      this.dispatchMakePartsEvent(fileContent, nameWithoutExtension);

      log("name without extension", nameWithoutExtension);
      const projectNameInput = document.getElementById("toolbar-project-name");
      const projectNameContainer = document.getElementById("toolbar-project-name-container");
      
      if (projectNameInput) {
        projectNameInput.value = nameWithoutExtension;
      }
      if (projectNameContainer) {
        projectNameContainer.style.display = "flex";
      }
    };
    
    reader.onerror = (error) => {
      log("Error reading file:", error);
      this.eventHub.emit("modalInfo", {
        title: "Error",
        message: "Failed to read the file. Please try again.",
        actions: [
          {
            label: "OK",
            callback: () => {
              log("Error acknowledged");
            },
          },
        ],
        autoClose: false,
      });
    };
    
    reader.readAsText(file);
  }

  /**
   * Imports an example design based on the provided event name.
   * @param {string} eventName - The event triggering the example import.
   */
  async importExample(eventName) {
    let filePath;
    let exampleName;

    // Determine the file and name based on the event
    if (eventName.endsWith("transceiver")) {
      filePath = "/examples/transceiver.xmd";
      exampleName = "transceiver";
    } else if (eventName.endsWith("layers")) {
      filePath = "/examples/layers.xmd";
      exampleName = "layers";
    } else {
      log(`Unknown example: ${eventName}`);
      return;
    }

    try {
      // Fetch the file
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to load example: ${response.statusText}`);
      }

      const fileContent = await response.text();

      // Dispatch the event with file content and name
      this.dispatchMakePartsEvent(fileContent, exampleName);

      log(`Example ${exampleName} loaded successfully.`);
      document.getElementById("toolbar-project-name").value = exampleName;
      document.getElementById("toolbar-project-name-container").style.display =
        "flex";

      // Close the modal if triggered from a modal event
      if (eventName.startsWith("modal")) {
        document.getElementById("backgroundModal").style.display = "none";
      }
    } catch (error) {
      log(`Error loading example: ${error.message}`);
      this.eventHub.emit("modalInfo", {
        title: "Error",
        message: `Failed to load the example: ${error.message}`,
        actions: [{ label: "OK", callback: () => log("Error acknowledged.") }],
        autoClose: false,
      });
    }
  }

  /**
   * Dispatches an event to create parts based on the provided file content.
   * @param {string} fileContent - The content of the XMD file.
   * @param {string} nameWithoutExtension - The name of the file without the extension.
   */
  dispatchMakePartsEvent(fileContent, nameWithoutExtension) {
    
    try {
      this.resetAutoPopulateToggles();
      this.resetLayerSliders();
      let designData = JSON.parse(fileContent);
      this.eventHub.emit("deleteScene");
      if (designData.figures) {
        this.eventHub.emit("MakePartsLoaderEvent", {
          eventType: "load",
          data: designData,
        });
      }
      this.postDesignImportedMessage(designData, nameWithoutExtension);
    } catch (error) {
      // Handle any errors that occur during parsing or event emission
      console.error("Error processing design data:", error);
      this.eventHub.emit("error", {
        message: "There was an issue processing the design file.",
        details: error.message,
      });
    }
    setTimeout(() => {
      this.eventHub.emit("zoomAll");
    }, 1000);
  }

  /**
   * Sends a message to the parent window after a design is imported.
   * @param {Object} designData - The data of the imported design.
   * @param {string} designName - The name of the imported design.
   */
  postDesignImportedMessage(designData, designName) {
    const data = {
      event: "designImported",
      contents: JSON.stringify(designData),
      name: designName,
    };
    log("EVENTHUB EVENT: ", data);

    this.eventHub.emit("designImported", data);
  }

  /**
 * Resets all auto-populate feature toggles so they don't activate after import.
 */
  /**
 * Resets all auto-populate feature toggles so they don't activate after import.
 * Emits the 'off' state and unchecks associated UI checkboxes.
 */
resetAutoPopulateToggles() {
  const toggles = [
    { id: "gsg-switch", event: "gsg-switch" },
    { id: "control-switch", event: "control-switch" },
    { id: "walls-switch", event: "walls-switch" },
    { id: "lids-switch", event: "lids-switch" }
  ];

  toggles.forEach(({ id, event }) => {
    // Emit off state
    this.eventHub.emit(event, { enabled: false });

    // Uncheck the checkbox in UI
    const checkbox = document.getElementById(id);
    if (checkbox && checkbox.checked) {
      checkbox.checked = false;

      // Trigger 'change' event in case app logic listens to it
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  log("✅ Auto-populate toggles reset after import.");
}

/**
 * Resets all layer transparency sliders to 0.
 */
resetLayerSliders() {
  const sliderIds = [
    "Housing",
    "Shape",
    "Ctl",
    "GSG",
    "Probe",
    "Walls",
    "Lids",
    "TextBox",
    "explodedView"
  ];

  sliderIds.forEach((id) => {
    const slider = document.getElementById(id);
    if (slider) {
      slider.value = 0;

      // Trigger input or change event if your logic listens to it
      slider.dispatchEvent(new Event("input", { bubbles: true }));
    }
  });

  log("✅ Sliders reset to 0 after import.");
}


}

export default XmdLoader;
